import { Node } from 'reactflow'
import {
  Workflow,
  WorkflowNodeData,
  WorkflowNodeParameter,
  WorkflowPayload,
  WorkflowNodePayload,
  WorkflowNodeParameterMin
} from '../types'
import { isVersionedNode } from '../utils/components'
import { isJson } from '../utils/isJson'
import { produce } from 'immer'
import { stringToOptions } from '../utils/fields'

export const sanitizeCreateWorkflow = (workflow: Workflow) => {
  return produce(workflow, (draft) => {
    if ((workflow?.config?.nodes || [])?.length > 0) {
      draft.config.nodes = workflow.config?.nodes?.map((node) => {
        if (node.data?.name === 'native.saveastable') {
          node.data.inputs.forEach((input, inputIndex) => {
            if (input.name === 'destination') {
              node.data.inputs[inputIndex].value = undefined
            }
          })
        }

        return node
      })
    }
  })
}

export const sanitizeWorkflow = (workflow: Workflow) => {
  return produce(workflow, (draft) => {
    draft.config.nodes = workflow.config.nodes.map((node) => {
      if (!isVersionedNode(node)) {
        return node
      }

      return {
        ...node,
        data: {
          name: node.data.name,
          title: node.data.title,
          version: node.data.version,
          inputs: node.data.inputs.map((input) => ({
            name: input.name,
            value: input.value
          })),
          fromImportedFile: node.data.fromImportedFile
        }
      }
    }) as Node<WorkflowNodeData>[]
  })
}

const checkInput = (input: WorkflowNodeParameter | WorkflowNodeParameterMin) => {
  switch (input.name) {
    case 'conditions':
      return isJson(input.value)
        ? input
        : ({
            ...input,
            value: null
          } as unknown as WorkflowNodeParameter)
    default:
      return input
  }
}

const checkNodes = (originalNodes: Node<WorkflowNodePayload>[]) => {
  const nodes = JSON.parse(JSON.stringify(originalNodes))

  originalNodes.forEach((node, index) => {
    switch (node.data.name) {
      case 'native.casewhen':
        nodes[index] = produce(node, (draft) => {
          draft.data.inputs = node.data.inputs.map((i) => checkInput(i))
        })
        return
      case 'native.groupby':
        // eslint-disable-next-line no-case-declarations
        const columnInput = node.data.inputs.find((input) => input.name === 'columns')

        if (!columnInput || !columnInput.value) {
          return
        }

        // All following "cluster by" columns needs to be reset
        nodes.slice(index).forEach((subNode, subIndex) => {
          const inputIndex = subNode.data.inputs.findIndex((input) => input.name === 'optimizationcol')
          const input = subNode.data.inputs[inputIndex]

          if (input && input.value && !stringToOptions(columnInput.value).includes(input.value as string)) {
            const nextIndex = index + subIndex
            nodes[nextIndex].data.inputs[inputIndex].value = undefined
          }
        })

        return
      default:
        return
    }
  })

  return nodes
}

export const sanitizeWorkflowNodes = (workflow: WorkflowPayload): WorkflowPayload => {
  return produce(workflow, (draft) => {
    draft.config.nodes = checkNodes(workflow.config.nodes)
  })
}
